import type { DataPoint, ProviderFile } from "fileParsing/types";
import type { ParsedExport } from "@store/data/types";
import { FilterType } from "@store/filters/types";
import type { ApplicationState } from "@store/index";
import { useAppSelector } from "index";
import { useCallback, useEffect, useMemo, useState } from "react";
import { EventTypes } from "webworker/BaseWorker";
import { getExportWorkerByUUID } from "webworker/init";

const getProviderFiles = async (providerItems: ParsedExport[]): Promise<ProviderFile[]> => {
  const getFilesFromWorker = async (workerId: string): Promise<ProviderFile[] | undefined> => {
    const worker = await getExportWorkerByUUID(workerId)?.getWrangler();
    const files = await worker?.getFiles();

    return files?.data;
  };

  const providerFiles = await Promise.all(
    providerItems.filter((item) => item.active).map((item) => getFilesFromWorker(item.workerId)),
  );

  return providerFiles.reduce<ProviderFile[]>((result, files) => (files ? result.concat(files) : result), []);
};

export const getDataPoints = async (
  providerItems: ParsedExport[],
  filter: (dataPoint: DataPoint) => boolean,
): Promise<DataPoint[]> => {
  const dataPoints = await Promise.all(
    providerItems
      .filter((item) => item.active)
      .map(async (item) => {
        const worker = await getExportWorkerByUUID(item.workerId)?.getWrangler();

        return (await worker?.getDataPoints()) ?? [];
      }),
  );

  return dataPoints
    .reduce<DataPoint[]>((accumulator, providerDataPoints) => accumulator.concat(providerDataPoints.filter(filter)), [])
    .sort((a, b) => a.date.getTime() - b.date.getTime());
};

const getUnfilteredDataPoints = async (providerItems: ParsedExport[]): Promise<DataPoint[]> => {
  const dataPoints = await Promise.all(
    providerItems.map(async (item) => {
      const worker = await getExportWorkerByUUID(item.workerId)?.getWrangler();

      return (await worker?.getDataPoints()) ?? [];
    }),
  );

  return dataPoints.flat().sort((a, b) => a.date.getTime() - b.date.getTime());
};

export function useUnfilteredDatapoints(): DataPoint[] {
  const providerItems = useAppSelector((state) => state.data.exports);
  const [result, setResult] = useState<DataPoint[]>([]);

  useEffect(() => {
    let cancelled = false;

    getUnfilteredDataPoints(providerItems).then((datapoints) => {
      if (!cancelled) {
        setResult(datapoints);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [providerItems]);

  return result;
}

export type UnfilteredDatapointsPerProviderType = {
  providerId: string;
  value: DataPoint[];
  active: boolean;
  name: string;
  id: number;
};

export function useUnfilteredDatapointsPerProvider(): UnfilteredDatapointsPerProviderType[] {
  const providerItems = useAppSelector((state) => state.data.exports);
  const [result, setResult] = useState<UnfilteredDatapointsPerProviderType[]>([]);

  useEffect(() => {
    let cancelled = false;

    const datapointsPerProvider = providerItems.map(async (providerItem) => {
      const datapoints = await getUnfilteredDataPoints([providerItem]);

      return {
        providerId: providerItem.providerId,
        value: datapoints,
        active: providerItem.active,
        name: providerItem.fileProperties.fileName,
        id: providerItem.id,
      };
    });

    Promise.all(datapointsPerProvider).then((datapoints) => {
      if (!cancelled) {
        setResult(datapoints);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [providerItems]);

  return result;
}

export function useProviderFiles(exportId?: number): ProviderFile[] {
  const providerItems = useAppSelector((state) => state.data.exports);
  const wranglerItems = useAppSelector((state) => state.wrangler.items);
  const [result, setResult] = useState<ProviderFile[]>([]);

  const triggerUpdate = useCallback((event: Event) => {
    const customEvent = event as CustomEvent<ProviderFile[]>;
    setResult(customEvent.detail);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadProviderFiles = async () => {
      const files =
        exportId === undefined
          ? await getProviderFiles(providerItems.filter(({ active }) => active))
          : await getProviderFiles(providerItems.filter(({ id }) => id === exportId));

      if (!cancelled) {
        setResult(files);
      }
    };

    void loadProviderFiles();

    return () => {
      cancelled = true;
    };
  }, [exportId, providerItems]);

  useEffect(() => {
    const workerIds =
      exportId === undefined
        ? providerItems.filter(({ active }) => active).map(({ workerId }) => workerId)
        : providerItems.filter(({ id }) => id === exportId).map(({ workerId }) => workerId);

    for (const workerId of workerIds) {
      document.addEventListener(`${workerId}_wrangler_${EventTypes.DataReady}`, triggerUpdate);
    }

    return () => {
      for (const workerId of workerIds) {
        document.removeEventListener(`${workerId}_wrangler_${EventTypes.DataReady}`, triggerUpdate);
      }
    };
  }, [exportId, providerItems, triggerUpdate]);

  // Keep the selector dependency explicit, it documents why this hook rerenders.
  void wranglerItems;

  return result;
}

export function useDatapoints(): DataPoint[] {
  const providerItems = useAppSelector((state) => state.data.exports);
  const categories = useCategoryFilter();
  const search = useSearchFilter();
  const [result, setResult] = useState<DataPoint[]>([]);

  useEffect(() => {
    let cancelled = false;

    getDataPoints(providerItems, (dataPoint) => categories(dataPoint) && search(dataPoint)).then((datapoints) => {
      if (!cancelled) {
        setResult(datapoints);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [providerItems, categories, search]);

  return result;
}

export function useDatapointsSelectionFiltered(): DataPoint[] {
  const providerItems = useAppSelector((state) => state.data.exports);
  const categories = useCategoryFilter();
  const search = useSearchFilter();
  const selection = useSelectionFilter();
  const [result, setResult] = useState<DataPoint[]>([]);

  useEffect(() => {
    let cancelled = false;

    getDataPoints(
      providerItems,
      (dataPoint) => categories(dataPoint) && search(dataPoint) && selection(dataPoint),
    ).then((datapoints) => {
      if (!cancelled) {
        setResult(datapoints);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [providerItems, categories, search, selection]);

  return result;
}

/**
 * Returns datapoints from one provider.
 * If providerId is undefined, returns datapoints from all providers.
 */
export function useDatapointsFromProviderId(providerId: string | undefined): DataPoint[] {
  const providerItems = useAppSelector((state) => state.data.exports);

  const filteredProviderItems = useMemo(
    () => providerItems.filter((item) => providerId === undefined || item.providerId === providerId),
    [providerItems, providerId],
  );

  return useDatapointsFromProvider(filteredProviderItems, false);
}

export function useDatapointsFromExportId(
  exportId: number[] | number | undefined,
  selectionfiltered = true,
): DataPoint[] {
  const providerItems = useAppSelector((state) => state.data.exports);

  const ids = useMemo(() => (typeof exportId === "number" ? [exportId] : exportId), [exportId]);

  const filteredProviderItems = useMemo(
    () =>
      providerItems.filter(
        (item) => (ids !== undefined && ids.includes(item.id)) || (ids === undefined && item.active),
      ),
    [providerItems, ids],
  );

  return useDatapointsFromProvider(filteredProviderItems, selectionfiltered);
}

export function useDatapointsFromProvider(providerItems: ParsedExport[], selectionfiltered = true): DataPoint[] {
  const categories = useCategoryFilter();
  const search = useSearchFilter();
  const selection = useSelectionFilter();
  const [result, setResult] = useState<DataPoint[]>([]);

  useEffect(() => {
    let cancelled = false;

    getDataPoints(
      providerItems,
      (dataPoint) => categories(dataPoint) && search(dataPoint) && (selection(dataPoint) || !selectionfiltered),
    ).then((datapoints) => {
      if (!cancelled) {
        setResult(datapoints);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [providerItems, categories, search, selection, selectionfiltered]);

  return result;
}

function useSearchFilter(): (dataPoint: DataPoint) => boolean {
  const searchString = useAppSelector((state) => state.options.searchString);
  const search = searchString.toLowerCase();

  return useCallback(
    (dataPoint: DataPoint) =>
      search === "" ||
      dataPoint.text.toLowerCase().includes(search) ||
      dataPoint.category.toLowerCase().includes(search) ||
      dataPoint.subcategory.toLowerCase().includes(search),
    [search],
  );
}

function useCategoryFilter(): (dataPoint: DataPoint) => boolean {
  const categoryFilter = useAppSelector((state: ApplicationState) => state.filters[FilterType.categories]);

  const filterIds = useMemo(() => categoryFilter.filter((item) => item.value).map((item) => item.id), [categoryFilter]);

  return useCallback((dataPoint: DataPoint) => filterIds.includes(dataPoint.category), [filterIds]);
}

export function useSelectionFilter(): (dataPoint: DataPoint) => boolean {
  const selection = useAppSelector((state: ApplicationState) => state.filters.selection);

  return useCallback(
    (dataPoint: DataPoint) =>
      selection[0] && selection[1]
        ? dataPoint.date.getTime() >= selection[0].getTime() && dataPoint.date.getTime() <= selection[1].getTime()
        : true,
    [selection],
  );
}
